<template>
  <FormGroup
    class="login-otp-form"
    layout="vertical"
    :wrapper-col="{ span: 24 }"
    :model="loginCheck"
    @finish="submitLogin"
    :loading="tokenAuthenticateLoading"
  >
    <div class="login-otp-form__label" v-if="!isMobileScreen">
      {{ t("Please enter your OTP (One-Time Password) generated") }}
    </div>
    <a-form-item
      name="code"
      :rules="makeRequiredRule(t('Please enter OTP code'))"
      :class="[
        'login-otp-form__otp-input',
        !!tokenAuthParsedError && 'login-otp-form__otp-input--api-error',
      ]"
      :validateStatus="!!tokenAuthParsedError && 'error'"
    >
      <BasicOtpInput
        class="login-otp-form__basic-input"
        v-model:value="loginCheck.code"
        @on-complete="submitLogin"
        @update:value="resetErrorMessage"
      />
    </a-form-item>

    <div class="login-otp-form__error-message">
      <a-typography-text type="danger">
        {{ tokenAuthParsedError }}
      </a-typography-text>
    </div>

    <div class="login-otp-form__or-separator">
      {{ t("or") }}
    </div>

    <!-- Other OTP Options, no need to show the backup code message -->
    <SecurityProtocolOptions
      :disabled="tokenAuthenticateLoading"
      isNonAuthenticated
      hideBackupCodeOption
    />
    <a-row class="mt-50">
      <a-col :xs="24">
        <FormSubmitButton
          :loading="tokenAuthenticateLoading"
          :disabled="isBtnLoading"
        >
          {{ t("Submit") }}
        </FormSubmitButton>
      </a-col>
    </a-row>
  </FormGroup>
</template>

<style lang="scss">
.login-otp-form {
  &__error-message {
    line-height: 1.1 !important;
  }
  &__label {
    color: $input-label-color;
    font-size: $heading-6-size;
  }
  &__otp-input {
    margin: $margin-xlg 0 $non-auth-margin-sm;
    &--api-error {
      margin-bottom: $margin-xss;
    }
    .basic-otp-input .basic-otp-input__wrapper {
      max-width: 100%;
    }
    .ant-form-item-control {
      max-width: 100%;
    }
  }

  &__or-separator {
    text-align: center;
    color: $text-color-less;
    font-size: $body-font-size;
    margin-top: $margin-md;
    margin-bottom: $margin-md;
  }

  .form-submit-button {
    min-width: 100%;
  }

  @media screen and (max-width: $screen-sm) {
    &__otp-input {
      margin: 0 0 $non-auth-margin-sm;
    }
  }
}
</style>

<script lang="ts">
import { computed, defineComponent, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { makeRequiredRule } from "@/shared/utils/validators/commonValidators";
import FormGroup from "@/shared/components/forms/FormGroup.vue";
import FormSubmitButton from "@/shared/components/buttons/FormSubmitButton.vue";
import BasicOtpInput from "@/shared/components/forms/BasicOtpInput.vue";
import SecurityProtocolOptions from "@/shared/components/SecurityProtocolOptions.vue";
import { windowSizeSimpleStore } from "@/shared/store/simpleStore/windowSizeData";
import { loginSimpleStore } from "@/shared/store/simpleStore/loginData";
import { useTokenAuthenticate } from "@/shared/composables/api/useTokenAuthenticate";
import find from "lodash/find";
import { GqlError } from "@/shared/utils/graphql/errorHandler";
import { apiErrorCodes } from "@/shared/utils/constants";

export default defineComponent({
  setup(_props, { emit }) {
    const t = useI18n().t;
    const isMobileScreen = computed(() => windowSizeSimpleStore.isMobile);
    const tokenAuthParsedError = ref<string>("");
    const { tokenAuthenticateLoading, tokenAuthenticate } =
      useTokenAuthenticate();

    const loginCheck = reactive({
      code: "",
    });

    const submitLogin = async () => {
      resetErrorMessage();
      try {
        /**
         * provide boolean true only
         * if submitLogin is called for Login OTP
         *
         * see: submitLogin - showError
         */
        await tokenAuthenticate(
          loginSimpleStore.captcha,
          false,
          loginSimpleStore.username,
          loginSimpleStore.password,
          loginCheck.code,
          true
        );
      } catch (parsedError) {
        const hasGqlError = (parsedError as GqlError)?.errors?.length > 0;

        // check if parsed error is not a fallback error message
        if (hasGqlError) {
          // Specific error message for INVALID INPUT
          if (
            find((parsedError as GqlError)?.errors, [
              "code",
              apiErrorCodes.TWO_FACTOR_AUTH,
            ])
          ) {
            return (tokenAuthParsedError.value = t(
              "Your OTP is incorrect. Please try again"
            ));
          } else {
            // else, return error message returned by BE
            return (tokenAuthParsedError.value = (
              parsedError as GqlError
            )?.errors[0].message);
          }
        }

        // return fallback error message
        return (tokenAuthParsedError.value = String(parsedError));
      }
    };

    const goBack = () => {
      emit("goBack");
    };

    const resetErrorMessage = () => {
      tokenAuthParsedError.value = "";
    };

    return {
      t,
      loginCheck,
      submitLogin,
      goBack,
      isMobileScreen,
      tokenAuthenticateLoading,
      tokenAuthParsedError,
      resetErrorMessage,
    };
  },
  emits: ["goBack"],
  methods: {
    makeRequiredRule,
  },
  components: {
    FormSubmitButton,
    BasicOtpInput,
    FormGroup,
    SecurityProtocolOptions,
  },
});
</script>
